import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  FileUp,
  Search,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  AppDialog,
  BottomNav,
  FixedButton,
  HomeHeader,
  MobileShell,
  TopNav,
} from "./components";
import { FoProduct, useCurrentUser, useNotice, useNotices, useOrder, useOrders, useProduct, useProducts, useQuote, useQuotes } from "./api";
import { navigate } from "./router";
import { getApiToken, requestApi } from "../shared/api";
import { formatPhone, openPostcodeSearch } from "./utils";

const sanitizedHtml = (html: string) => DOMPurify.sanitize(html, {
  USE_PROFILES: { html: true },
});

const moneyValue = (value: unknown) => `${Number(value || 0).toLocaleString("ko-KR")}원`;
const PACKAGING_DRAFT_KEY = "sodamwon-packaging-draft";
const orderStatusLabel = (status: unknown) => ({
  ordered: "주문완료",
  confirmed: "주문확인",
  shipping: "배송중",
  delivered: "배송완료",
  completed: "확정",
  cancelled: "주문취소",
}[String(status)] || String(status || "-"));
const paymentStatusLabel = (status: unknown) => ({
  ready: "결제대기",
  pending: "승인중",
  done: "결제완료",
  failed: "결제실패",
  cancelled: "결제취소",
  refunded: "환불완료",
}[String(status)] || orderStatusLabel(status));

interface PackagingDraft {
  purpose: string;
  mood: string;
  budget: string;
  customBudget: string;
  quantity: string;
  customQuantity: string;
  additionalRequest?: string;
  attachmentFileId?: string;
  aiGenerationId?: string;
  recommendationName?: string;
  recommendationDirection?: string;
  recommendationComposition?: string;
  recommendationPoints?: string;
  recommendationRationale?: string;
}

interface PackagingRecommendation {
  generationId: string;
  recommendationName: string;
  recommendationDirection: string;
  recommendationComposition: string;
  recommendationPoints: string;
  rationale: string;
}

let packagingGenerationPromise: Promise<PackagingRecommendation> | null = null;

function readPackagingDraft(): Partial<PackagingDraft> {
  try { return JSON.parse(sessionStorage.getItem(PACKAGING_DRAFT_KEY) || "{}"); }
  catch { return {}; }
}

function budgetRange(value = "", custom = "") {
  if (value === "직접 입력") return { budgetMin: Number(custom) || undefined, budgetMax: Number(custom) || undefined };
  if (value === "3만원 이하") return { budgetMin: 0, budgetMax: 30000 };
  if (value === "3~5만원") return { budgetMin: 30000, budgetMax: 50000 };
  if (value === "5~10만원") return { budgetMin: 50000, budgetMax: 100000 };
  if (value === "10만원 이상") return { budgetMin: 100000, budgetMax: undefined };
  return {};
}

function quantityValue(value = "", custom = "") {
  if (value === "직접 입력") return Number(custom.replace(/\D/g, "")) || undefined;
  if (value === "1~5개") return 5;
  if (value === "6~20개") return 20;
  if (value === "21~50개") return 50;
  if (value === "51개 이상") return 51;
  return Number(value.replace(/\D/g, "")) || undefined;
}

declare global {
  interface Window {
    TossPayments?: (clientKey: string) => {
      widgets: (options: { customerKey: string }) => {
        setAmount: (amount: { currency: "KRW"; value: number }) => Promise<void>;
        renderPaymentWindow: () => Promise<{
          on: (eventName: "paymentRequest", callback: () => void | Promise<void>) => void;
          destroy: () => Promise<void>;
        }>;
        requestPayment: (options: Record<string, unknown>) => Promise<void>;
      };
      payment: (options: { customerKey: string }) => {
        requestPayment: (options: Record<string, unknown>) => Promise<void>;
      };
    };
  }
}

let tossLoader: Promise<void> | null = null;
function loadTossPayments() {
  if (window.TossPayments) return Promise.resolve();
  if (tossLoader) return tossLoader;
  tossLoader = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v2/standard";
    script.async = true;
    script.onload = () => window.TossPayments ? resolve() : reject(new Error("결제창을 불러오지 못했습니다."));
    script.onerror = () => reject(new Error("결제창을 불러오지 못했습니다."));
    document.head.appendChild(script);
  }).catch((error) => { tossLoader = null; throw error; });
  return tossLoader;
}

function AuthenticatedImage({ src, fallback, alt }: { src?: string; fallback: string; alt: string }) {
  const [resolvedSrc, setResolvedSrc] = useState(fallback);
  useEffect(() => {
    if (!src) { setResolvedSrc(fallback); return; }
    const controller = new AbortController();
    let objectUrl = "";
    const token = getApiToken();
    fetch(src, { signal: controller.signal, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((response) => {
        if (!response.ok) throw new Error("이미지를 불러오지 못했습니다.");
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => { if (!controller.signal.aborted) setResolvedSrc(fallback); });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fallback, src]);
  return <img src={resolvedSrc} alt={alt} />;
}

export function HomeScreen() {
  const { data: featuredProducts, loading } = useProducts({ pageSize: 3 });
  const { data: quotes } = useQuotes();
  const latestQuote = quotes[0];
  return (
    <MobileShell className="home-screen" bottomNav>
      <section className="home-hero">
        <img src="/assets/home-hero-20260814.webp" alt="보자기 선물과 전통 한과" />
        <div className="hero-shade" />
        <HomeHeader light />
        <div className="hero-copy">
          <h1>정성을 담은 한과</h1>
          <p>
            고객님의 취향과 목적에 맞는 한과를 더욱 쉽고 편리하게 준비하실 수 있도록, AI 추천부터 전문 상담, 견적과 계약까지 모든 과정을 체계적으로 안내해 드립니다.
          </p>
          <button type="button" onClick={() => navigate("packaging")}>
            시작하기 <ChevronRight size={14} />
          </button>
        </div>
      </section>
      {latestQuote && <section className="home-section notice-strip">
        <strong>최근 견적</strong>
        <button type="button" onClick={() => navigate("quoteDetail", { id: latestQuote.id })}>
          <span className="notice-copy">
            <b>{latestQuote.productName}</b>
            <small>{latestQuote.status === "quoted" ? "견적 수신 | 확인 필요" : "견적 요청 | 진행 중"}</small>
          </span>
          <span>상세보기</span>
        </button>
      </section>}
      <section className="home-section story-section">
        <p className="section-kicker">Story</p>
        <p>
          한과는 우리 고유의 맛과 정성을 담은 전통 간식입니다.
          <br />
          좋은 재료와 정성스러운 손길로 빚어낸 한과는
          <br />
          온전한 달콤함과 바삭한 식감으로
          <br />
          세대를 이어 사랑받아 왔습니다.
        </p>
        <p className="story-quote">
          소중한 사람과 함께하는 순간,
          <br />
          한과의 깊은 풍미를 전해보세요.
        </p>
        <img
          src="/assets/home-story.png"
          alt="정성스럽게 상품을 포장하는 모습"
        />
        <p className="story-action">
          다양한 한과 상품을 탐색해 보세요
          <br />
          <button
            className="inline-link"
            type="button"
            onClick={() => navigate("productHome")}
          >
            쇼핑 <ChevronRight size={12} />
          </button>
        </p>
        <p className="story-action">
          AI로 견적을 구성해 보세요
          <br />
          <button
            className="inline-link"
            type="button"
            onClick={() => navigate("packaging")}
          >
            AI 패키징 <ChevronRight size={12} />
          </button>
        </p>
      </section>
      <section className="home-section products-section">
        <h2 className="block-title">추천 상품</h2>
        <ProductShowcase items={featuredProducts} loading={loading} />
      </section>
      <section className="home-section process-section">
        <p className="section-kicker">Our Process</p>
        <p>
          고객님의 취향과 목적에 맞는 한과를
          <br />
          더욱 쉽고 편리하게 준비하실 수 있도록,
          <br />
          AI 추천부터 전문 상담, 견적과 계약까지
          <br />
          모든 과정을 체계적으로 안내해 드립니다.
        </p>
        <img src="/assets/home-process.png" alt="다양한 한과 선물 포장" />
        <ol>
          <li>
            <b>01</b>
            <span>
              <strong>AI 구상 시작</strong>
              <small>예산과 분위기, 원하는 구성을 입력해 주세요.</small>
            </span>
          </li>
          <li>
            <b>02</b>
            <span>
              <strong>AI 분석 확인</strong>
              <small>맞춤 구성과 패키지를 추천해 드립니다.</small>
            </span>
          </li>
          <li>
            <b>03</b>
            <span>
              <strong>견적 요청</strong>
              <small>수량과 납품 일정을 확인하고 견적을 요청해 주세요.</small>
            </span>
          </li>
          <li>
            <b>04</b>
            <span>
              <strong>견적 확인</strong>
              <small>전문 상담원이 맞춤 견적을 안내해 드립니다.</small>
            </span>
          </li>
          <li>
            <b>05</b>
            <span>
              <strong>계약 진행</strong>
              <small>최종 확인 후 맞춤 제작을 시작합니다.</small>
            </span>
          </li>
        </ol>
      </section>
      <footer className="fo-footer">
        <strong>주식회사 그린에이솔루션</strong>
        <div>
          대표이사 : 이도열
          <br />
          경기도 화성시 동탄구 동탄중심상가1길 33, 9층 901-14호(반송동,
          동탄미래도프라자)
          <br />
          사업자등록번호 : 369-87-03886
        </div>
      </footer>
    </MobileShell>
  );
}

function ProductShowcase({ items, loading = false }: { items: FoProduct[]; loading?: boolean }) {
  if (loading) return <div className="product-showcase product-data-state">상품을 불러오는 중입니다.</div>;
  if (!items.length) return <div className="product-showcase product-data-state">등록된 상품이 없습니다.</div>;
  return (
    <div className="product-showcase">
      <button
        type="button"
        className="product-showcase__main"
        onClick={() => navigate("productDetail", { id: items[0].id })}
      >
        <img src={items[0].image} alt="" />
        <span>{items[0].category}</span>
        <strong>{items[0].name}</strong>
        {items[0].regularPrice > items[0].salePrice && <del>{items[0].regularPrice.toLocaleString("ko-KR")}원</del>}
        <b>{items[0].price}</b>
      </button>
      <div className="product-showcase__small">
        {items.slice(1, 3).map((item) => (
          <button
            type="button"
            key={item.id}
            onClick={() => navigate("productDetail", { id: item.id })}
          >
            <img src={item.image} alt="" />
            <span>{item.category}</span>
            <strong>{item.name}</strong>
            {item.regularPrice > item.salePrice && <del>{item.regularPrice.toLocaleString("ko-KR")}원</del>}
            <b>{item.price}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ProductHomeScreen() {
  const { data: products, loading } = useProducts({ pageSize: 6 });
  const recommended = products.slice(0, 3);
  const best = products.slice(3, 6).length ? products.slice(3, 6) : products.slice(0, 3);
  return (
    <MobileShell className="product-home-screen" bottomNav>
      <section className="product-home-hero">
        <img src="/assets/product-home-hero.png" alt="정성스럽게 포장한 선물" />
        <div className="hero-shade" />
        <div className="product-home-header">
          <img
            className="product-home-header__logo"
            src="/assets/sodamwon-logo-official-light.png"
            alt="소담원"
          />
          <button
            type="button"
            onClick={() => navigate("productSearch")}
            aria-label="상품 검색"
          >
            <Search size={22} />
          </button>
        </div>
        <div className="product-home-copy">
          <h1>시간이 빚어낸 전통의 맛</h1>
          <p>
            한과는 자연이 준 재료와 정성스러운 제조 과정을 거쳐,
            <br />
            화려하지 않아도 오래 기억되는 맛을 선사합니다.
          </p>
          <div className="product-home-actions">
            <button type="button" onClick={() => navigate("shop")}>
              상품 전체보기
            </button>
            <button type="button" onClick={() => navigate("productSearch")}>
              검색 <Search size={13} />
            </button>
          </div>
        </div>
      </section>
      <section className="product-home-section product-home-section--recommended">
        <h2 className="block-title">추천 상품</h2>
        <ProductShowcase items={recommended} loading={loading} />
      </section>
      <section className="product-promo">
        <img src="/assets/product-home-promo.png" alt="좋은 재료로 만든 한과" />
        <div>
          <h2>좋은 재료와 정성으로</h2>
          <p>
            고객님의 취향과 목적에 맞는 한과를
            <br />
            더욱 쉽고 편리하게 준비하실 수 있도록 함께합니다.
          </p>
        </div>
      </section>
      <section className="product-home-section product-home-section--best">
        <h2 className="block-title">베스트</h2>
        <ProductShowcase items={best} loading={loading} />
      </section>
    </MobileShell>
  );
}

export function ProductSearchScreen() {
  const [keyword, setKeyword] = useState("");
  const [recentKeywords, setRecentKeywords] = useState([
    "약과",
    "한과",
    "강정",
  ]);
  const recommendedKeywords = ["한과", "선물세트", "답례품"];

  const searchProducts = (value: string) => {
    const query = value.trim();
    if (!query) return;
    setRecentKeywords((current) => [
      query,
      ...current.filter((item) => item !== query),
    ]);
    navigate("shop", { q: query });
  };

  return (
    <MobileShell className="product-search-screen">
      <TopNav title="검색" fallback="productHome" />
      <section className="product-search-content">
        <div className="product-search-form">
          <h1>
            찾으시는 상품이
            <br />
            있으신가요?
          </h1>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              searchProducts(keyword);
            }}
          >
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="물품을 검색해 주세요."
              aria-label="상품 검색어"
            />
            <button type="submit" aria-label="검색 실행">
              <Search size={28} strokeWidth={1.5} />
            </button>
          </form>
        </div>
        <section className="keyword-section">
          <header>
            <h2>최근 검색어</h2>
            <button type="button" onClick={() => setRecentKeywords([])}>
              전체삭제
            </button>
          </header>
          <div className="keyword-chips">
            {recentKeywords.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => searchProducts(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </section>
        <section className="keyword-section">
          <header>
            <h2>추천 검색어</h2>
          </header>
          <div className="keyword-chips">
            {recommendedKeywords.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => searchProducts(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </section>
      </section>
    </MobileShell>
  );
}

const categories = [
  "전체",
  "약과",
  "유과/산자",
  "강정/엿강정",
  "정과/다식",
  "전병/오란다",
  "선물세트",
  "답례품",
  "기타",
];
export function ShopScreen() {
  const [category, setCategory] = useState("전체");
  const query = new URL(window.location.href).searchParams.get("q")?.trim();
  const { data: visibleProducts, loading, error } = useProducts({ q: query, category });
  return (
    <MobileShell className="shop-screen">
      <section className="shop-hero">
        <img src="/assets/shop-hero.png" alt="전통 한과가 담긴 소반" />
        <div className="hero-shade" />
        <TopNav fallback="productHome" tone="light" />
        <h1>Shop</h1>
      </section>
      <nav className="category-tabs">
        {categories.map((item) => (
          <button
            type="button"
            key={item}
            className={category === item ? "is-active" : ""}
            onClick={() => setCategory(item)}
          >
            {item}
          </button>
        ))}
      </nav>
      <section className="product-grid">
        {loading && <p className="product-data-state">상품을 불러오는 중입니다.</p>}
        {!loading && error && <p className="product-data-state">{error}</p>}
        {!loading && !error && !visibleProducts.length && <p className="product-data-state">조건에 맞는 상품이 없습니다.</p>}
        {visibleProducts.map((product) => (
          <button
            className="product-card"
            type="button"
            key={product.id}
            onClick={() => navigate("productDetail", { id: product.id })}
          >
            <img src={product.image} alt="" />
            <span>{product.category}</span>
            <strong>{product.name}</strong>
            <span className="product-card-price">
              {product.regularPrice > product.salePrice && <del>{product.regularPrice.toLocaleString("ko-KR")}원</del>}
              <b>{product.price}</b>
            </span>
          </button>
        ))}
      </section>
    </MobileShell>
  );
}

export function ProductDetailScreen() {
  const id = new URL(window.location.href).searchParams.get("id");
  const { data: product, loading, error } = useProduct(id);
  if (loading) return <MobileShell className="detail-screen"><TopNav fallback="shop" /><p className="product-data-state">상품을 불러오는 중입니다.</p></MobileShell>;
  if (!product || error) return <MobileShell className="detail-screen"><TopNav fallback="shop" /><p className="product-data-state">{error || "상품을 찾을 수 없습니다."}</p></MobileShell>;
  return (
    <MobileShell className="detail-screen">
      <TopNav fallback="shop" />
      <div className="product-image-carousel" aria-label="상품 이미지">
        {(product.images?.length ? product.images : [{ id: "main", url: product.image, imageRole: "main" }]).map((image) => (
          <img className="detail-cover" key={image.id} src={image.url} alt={product.name} />
        ))}
      </div>
      <section className="detail-summary">
        <span>{product.category}</span>
        <h1>{product.name}</h1>
        {product.regularPrice > product.salePrice && <del>{product.regularPrice.toLocaleString("ko-KR")}원</del>}
        <strong>{product.price}</strong>
      </section>
      <div className="detail-body">
        {product.detailHtml ? <div className="detail-html" dangerouslySetInnerHTML={{ __html: sanitizedHtml(product.detailHtml) }} /> : (
          <img className="detail-long-image" src="/assets/product-detail-long.png" alt="상품 상세 설명" />
        )}
      </div>
      <div className="static-action product-detail-action">
        <button type="button" onClick={() => navigate("payment", { productId: product.id })}>주문하기</button>
      </div>
    </MobileShell>
  );
}

export function NoticesScreen() {
  const { data: notices, loading, error } = useNotices();
  return (
    <MobileShell>
      <TopNav title="공지사항" fallback="home" />
      <div className="list-page">
        {loading && <p className="product-data-state">공지사항을 불러오는 중입니다.</p>}
        {!loading && error && <p className="product-data-state">{error}</p>}
        {!loading && !error && !notices.length && <p className="product-data-state">등록된 공지사항이 없습니다.</p>}
        {notices.map((n) => (
          <button
            type="button"
            className="plain-list-row"
            key={n.id}
            onClick={() => navigate("noticeDetail", { id: n.id })}
          >
            <strong>{n.title}</strong>
            <span>{n.date}</span>
          </button>
        ))}
      </div>
    </MobileShell>
  );
}
export function NoticeDetailScreen() {
  const id = new URL(location.href).searchParams.get("id");
  const { data: n, loading, error } = useNotice(id);
  if (loading) return <MobileShell><TopNav title="공지사항" fallback="notices" /><p className="product-data-state">공지사항을 불러오는 중입니다.</p></MobileShell>;
  if (!n || error) return <MobileShell><TopNav title="공지사항" fallback="notices" /><p className="product-data-state">{error || "공지사항을 찾을 수 없습니다."}</p></MobileShell>;
  return (
    <MobileShell>
      <TopNav title="공지사항" fallback="notices" />
      <article className="article-detail">
        <h1>{n.title}</h1>
        <time>{n.date}</time>
        <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizedHtml(n.bodyHtml || "") }} />
      </article>
    </MobileShell>
  );
}

export function PackagingScreen() {
  return (
    <MobileShell className="packaging-landing" bottomNav>
      <img
        src="/assets/packaging-hero.png"
        alt="전통 한옥 장식과 한과 패키지"
      />
      <div className="packaging-landing__shade" />
      <section className="packaging-landing__copy">
        <h1>
          패키징 추천부터
          <br />
          견적까지 한 공간에서,
        </h1>
        <p>
          AI 추천과 전문 상담을 통해 예산과 용도에 맞는
          <br />
          한과를 제안하고, 견적부터 계약까지
          <br />
          빠르고 편리한 과정을 제공합니다.
        </p>
        <button type="button" onClick={() => navigate("packagingForm")}>
          시작하기 <ChevronRight size={16} />
        </button>
      </section>
    </MobileShell>
  );
}

export function PackagingFormScreen() {
  const [purpose, setPurpose] = useState("");
  const [mood, setMood] = useState("");
  const [budget, setBudget] = useState("");
  const [customBudget, setCustomBudget] = useState("");
  const [quantity, setQuantity] = useState("");
  const [customQuantity, setCustomQuantity] = useState("");
  const [additionalRequest, setAdditionalRequest] = useState("");
  const groups = [
    {
      title: "1. 용도를 골라주세요.",
      value: purpose,
      setValue: setPurpose,
      options: ["답례품", "명절", "기업", "생일/기념일", "상견례/행사"],
    },
    {
      title: "2. 분위기를 선택해 주세요.",
      value: mood,
      setValue: setMood,
      options: ["전통적인", "고급스러운", "미니멀한", "화사한", "정갈한"],
    },
    {
      title: "3. 예산을 선택해 주세요.",
      value: budget,
      setValue: setBudget,
      options: [
        "3만원 이하",
        "3~5만원",
        "5~10만원",
        "10만원 이상",
        "직접 입력",
      ],
    },
    {
      title: "4. 수량을 선택해 주세요.",
      value: quantity,
      setValue: setQuantity,
      options: ["1~5개", "6~20개", "21~50개", "51개 이상", "직접 입력"],
    },
  ];
  return (
    <MobileShell className="packaging-form-screen">
      <TopNav title="정보 입력" fallback="packaging" />
      <section className="screen-content">
        <h2>
          판매할 상품을 구상하기 위해
          <br />
          정보를 입력해 주세요.
        </h2>
        <p className="screen-lead">
          상품 구성 결과를 본 후 견적 요청까지 진행 가능합니다.
        </p>
        <div className="choice-form">
          {groups.map((group) => (
            <fieldset key={group.title}>
              <legend>
                {group.title}
                <b>*</b>
              </legend>
              <div className="choice-grid">
                {group.options.map((option) => (
                  <button
                    type="button"
                    key={option}
                    className={group.value === option ? "is-active" : ""}
                    onClick={() => group.setValue(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {group.title.startsWith("3.") && budget === "직접 입력" && (
                <div className="direct-budget-field">
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={customBudget}
                    onChange={(event) => setCustomBudget(event.target.value)}
                    placeholder="예산을 입력해 주세요."
                    aria-label="직접 예산 입력"
                  />
                  <span>원</span>
                </div>
              )}
              {group.title.startsWith("4.") && quantity === "직접 입력" && (
                <div className="direct-budget-field direct-quantity-field">
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={customQuantity}
                    onChange={(event) => setCustomQuantity(event.target.value)}
                    placeholder="수량을 입력해 주세요."
                    aria-label="직접 수량 입력"
                  />
                  <span>개</span>
                </div>
              )}
            </fieldset>
          ))}
          <fieldset>
            <legend>5. 추가 요청사항을 입력해 주세요.</legend>
            <textarea value={additionalRequest} onChange={(event) => setAdditionalRequest(event.target.value)} placeholder="내용을 입력해 주세요." />
          </fieldset>
        </div>
      </section>
      <div className="static-action">
        <button
          type="button"
          disabled={
            !purpose ||
            !mood ||
            !budget ||
            !quantity ||
            (budget === "직접 입력" && !customBudget) ||
            (quantity === "직접 입력" && !customQuantity)
          }
          onClick={() => {
            sessionStorage.setItem(PACKAGING_DRAFT_KEY, JSON.stringify({ purpose, mood, budget, customBudget, quantity, customQuantity, additionalRequest } satisfies PackagingDraft));
            navigate("upload");
          }}
        >
          다음
        </button>
      </div>
    </MobileShell>
  );
}

export function UploadScreen() {
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  return (
    <MobileShell className="upload-screen has-fixed-action">
      <TopNav title="파일 첨부" fallback="packagingForm" />
      <section className="upload-content">
        <h2>
          패키징 생성에 참고할 자료를
          <br />
          첨부해 주세요.
        </h2>
        <p>
          첨부파일 없이도 제공 가능하지만,
          <br />
          정확도가 낮아질 수 있습니다.
        </p>
        <label className="upload-zone">
          <FileUp size={24} strokeWidth={1.6} />
          <strong>{name || "파일 선택하기"}</strong>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
            onChange={(e) => {
              const next = e.target.files?.[0] || null;
              setError("");
              if (next && next.size > 3 * 1024 * 1024) {
                setFile(null); setName(""); setError("첨부파일은 3MB 이하만 등록할 수 있습니다.");
                e.target.value = "";
                return;
              }
              setFile(next); setName(next?.name || "");
            }}
          />
        </label>
        <small>jpg, png, pdf, docx 파일 (최대 3mb)</small>
        {error && <p className="form-error">{error}</p>}
      </section>
      <FixedButton disabled={uploading} onClick={async () => {
        setUploading(true); setError("");
        try {
          if (file) {
            const form = new FormData();
            form.append("file", file);
            form.append("ownerType", "quote-request");
            const uploaded = await requestApi<{ id: string }>("/files", { method: "POST", body: form });
            sessionStorage.setItem(PACKAGING_DRAFT_KEY, JSON.stringify({ ...readPackagingDraft(), attachmentFileId: uploaded.id }));
          }
          packagingGenerationPromise = null;
          navigate("uploadLoading");
        } catch (reason) {
          setError(reason instanceof Error ? reason.message : "파일 업로드에 실패했습니다.");
        } finally { setUploading(false); }
      }}>{uploading ? "업로드 중" : "다음"}</FixedButton>
    </MobileShell>
  );
}

export function UploadLoadingScreen() {
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const draft = readPackagingDraft();
    if (draft.aiGenerationId && draft.recommendationName) {
      navigate("recommendation");
      return () => { active = false; };
    }
    const budget = budgetRange(draft.budget, draft.customBudget);
    if (!packagingGenerationPromise) {
      packagingGenerationPromise = requestApi<PackagingRecommendation>("/ai/packaging", {
        method: "POST",
        body: JSON.stringify({
          purpose: draft.purpose,
          mood: draft.mood,
          ...budget,
          quantity: quantityValue(draft.quantity, draft.customQuantity),
          additionalRequest: draft.additionalRequest,
          attachmentFileId: draft.attachmentFileId,
        }),
      });
    }
    packagingGenerationPromise.then((result) => {
      sessionStorage.setItem(PACKAGING_DRAFT_KEY, JSON.stringify({
        ...readPackagingDraft(),
        purpose: draft.purpose || "",
        mood: draft.mood || "",
        budget: draft.budget || "",
        customBudget: draft.customBudget || "",
        quantity: draft.quantity || "",
        customQuantity: draft.customQuantity || "",
        aiGenerationId: result.generationId,
        recommendationName: result.recommendationName,
        recommendationDirection: result.recommendationDirection,
        recommendationComposition: result.recommendationComposition,
        recommendationPoints: result.recommendationPoints,
        recommendationRationale: result.rationale,
      } satisfies PackagingDraft));
      if (active) navigate("recommendation");
    }).catch((reason) => {
      packagingGenerationPromise = null;
      if (active) setError(reason instanceof Error ? reason.message : "AI 추천 생성에 실패했습니다.");
    });
    return () => { active = false; };
  }, [attempt]);

  return (
    <MobileShell className="upload-loading-screen">
      {!error && <div className="loading-indicator" aria-hidden="true" />}
      <h1>패키징 구상안을 만들고 있어요.</h1>
      <p>
        {error ? error : <>첨부한 자료와 입력 정보를 분석하고 있습니다.<br />잠시만 기다려 주세요.</>}
      </p>
      {error && <button className="loading-retry-button" type="button" onClick={() => { setError(""); setAttempt((value) => value + 1); }}>다시 시도</button>}
    </MobileShell>
  );
}

export function RecommendationScreen() {
  const draft = readPackagingDraft();
  if (!draft.aiGenerationId || !draft.recommendationName) {
    return (
      <MobileShell className="recommend-screen has-fixed-action">
        <TopNav title="추천 결과" fallback="packagingForm" />
        <p className="product-data-state">생성된 추천 결과가 없습니다.</p>
        <FixedButton onClick={() => navigate("uploadLoading")}>추천 다시 생성</FixedButton>
      </MobileShell>
    );
  }
  return (
    <MobileShell className="recommend-screen">
      <header className="result-nav">
        <h1>추천 결과</h1>
        <button
          type="button"
          onClick={() => navigate("packaging")}
          aria-label="닫기"
        >
          <X size={24} strokeWidth={1.5} />
        </button>
      </header>
      <section className="recommend-content">
        <h2>추천 구상안이 생성되었습니다.</h2>
        <p>입력한 판매 채널과 타겟을 기준으로 생성된 결과입니다.</p>
        <dl className="recommend-result">
          <div>
            <dt>제품명</dt>
            <dd>{draft.recommendationName}</dd>
          </div>
          <div>
            <dt>패키지 방향</dt>
            <dd>
              {draft.recommendationDirection}
            </dd>
          </div>
          <div>
            <dt>추천 구성</dt>
            <dd className="recommend-tags">
              {(draft.recommendationComposition || "").split(",").filter(Boolean).map((item) => <span key={item}>{item.trim()}</span>)}
            </dd>
          </div>
          <div>
            <dt>패키징 포인트</dt>
            <dd className="recommend-points">
              {draft.recommendationPoints
                ? <ol>{draft.recommendationPoints.split(/\s*(?:\/|\n|\d+[.)])\s*/).map((item) => item.trim()).filter(Boolean).map((item) => <li key={item}>{item}</li>)}</ol>
                : "-"}
            </dd>
          </div>
        </dl>
      </section>
      <div className="recommend-action">
        <button type="button" onClick={() => navigate("quoteRequest")}>견적 요청</button>
      </div>
    </MobileShell>
  );
}

export function QuoteRequestScreen() {
  const [deliveryDate, setDeliveryDate] = useState("");
  const [completed, setCompleted] = useState(false);
  const [requestText, setRequestText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dateInputRef = useRef<HTMLInputElement>(null);
  const today = new Date();
  const todayIso = new Date(
    today.getTime() - today.getTimezoneOffset() * 60_000,
  )
    .toISOString()
    .slice(0, 10);
  const draft = readPackagingDraft();

  return (
    <MobileShell className="quote-request-screen has-fixed-action">
      <header className="result-nav">
        <h1>견적 요청</h1>
        <button
          type="button"
          onClick={() => navigate("recommendation")}
          aria-label="닫기"
        >
          <X size={24} strokeWidth={1.5} />
        </button>
      </header>
      <section className="quote-request-content">
        <h2>
          구상한 패키징으로
          <br />
          견적을 요청합니다.
        </h2>
        <p>견적 생성에 필요한 부가 정보를 입력해 주세요.</p>
        <div className="field-stack">
          <label>
            1. 희망 납기일을 선택해 주세요.<b>*</b>
          </label>
          <div
            className="date-select"
            onClick={() => dateInputRef.current?.showPicker?.()}
          >
            <CalendarDays size={18} />
            <input
              ref={dateInputRef}
              type="date"
              min={todayIso}
              value={deliveryDate}
              onClick={() => dateInputRef.current?.showPicker?.()}
              onFocus={() => dateInputRef.current?.showPicker?.()}
              onChange={(event) => setDeliveryDate(event.target.value)}
              aria-label="희망 납기일"
            />
            <ChevronDown size={18} />
          </div>
          <label>2. 추가 요청사항을 입력해 주세요.</label>
          <textarea value={requestText} onChange={(event) => setRequestText(event.target.value)} placeholder="내용을 입력해 주세요." />
        </div>
      </section>
      <FixedButton disabled={!deliveryDate || saving} onClick={async () => {
        setSaving(true); setError("");
        try {
          const budget = budgetRange(draft.budget, draft.customBudget);
          await requestApi("/quotes", { method: "POST", body: JSON.stringify({
            productName: draft.recommendationName || "AI 추천 패키징", purpose: draft.purpose || "맞춤 패키징",
            mood: draft.mood, ...budget, quantity: quantityValue(draft.quantity, draft.customQuantity), desiredDeliveryDate: deliveryDate,
            additionalRequest: requestText || draft.additionalRequest, attachmentFileId: draft.attachmentFileId,
            recommendationName: draft.recommendationName, recommendationDirection: draft.recommendationDirection,
            recommendationComposition: draft.recommendationComposition, recommendationPoints: draft.recommendationPoints,
            aiGenerationId: draft.aiGenerationId,
          }) });
          setCompleted(true);
        } catch (reason) { setError(reason instanceof Error ? reason.message : "견적 요청에 실패했습니다."); }
        finally { setSaving(false); }
      }}>
        완료
      </FixedButton>
      {error && <p className="form-error fixed-action-error">{error}</p>}
      {completed && (
        <AppDialog
          title="견적 요청이 완료되었습니다."
          description="담당자가 내용을 확인한 후 견적을 안내해 드립니다."
          onConfirm={() => { sessionStorage.removeItem(PACKAGING_DRAFT_KEY); navigate("quotes", { source: "created" }); }}
        />
      )}
    </MobileShell>
  );
}

export function QuotesScreen() {
  const [filter, setFilter] = useState("전체");
  const filters = ["전체", "요청중", "견적 도착", "계약 상담"];
  const { data: quoteItems, loading, error } = useQuotes();
  const statusLabel = (status: string) => ({ requested: "요청중", consulting: "계약 상담", quoted: "견적 도착", paid: "결제완료", cancelled: "취소" }[status] || status);
  const visible =
    filter === "전체"
      ? quoteItems
      : quoteItems.filter((item) => statusLabel(item.status) === filter);
  const source = new URL(window.location.href).searchParams.get("source");
  return (
    <MobileShell className="quotes-screen">
      <TopNav title="견적 요청 내역" fallback={source === "created" ? "home" : "my"} forceFallback={source === "created"} />
      <div className="quote-filters">
        {filters.map((item) => (
          <button
            type="button"
            key={item}
            className={filter === item ? "is-active" : ""}
            onClick={() => setFilter(item)}
          >
            {item}
          </button>
        ))}
      </div>
      <div className="quote-list">
        {loading && <p className="product-data-state">견적 내역을 불러오는 중입니다.</p>}
        {!loading && error && <p className="product-data-state">{error}</p>}
        {!loading && !error && !visible.length && <p className="product-data-state">견적 요청 내역이 없습니다.</p>}
        {visible.map((item) => (
          <article key={item.id}>
            <header>
              <strong>{item.createdAt.slice(0, 10)}</strong>
              <button type="button" onClick={() => navigate("quoteDetail", { id: item.id })}>
                상세보기
              </button>
            </header>
            <span>{statusLabel(item.status)}</span>
            <button
              className="quote-product"
              type="button"
              onClick={() => navigate("quoteDetail", { id: item.id })}
            >
              <AuthenticatedImage src={item.attachmentImageUrl} fallback="/assets/product-3.png" alt={`${item.productName} 견적 첨부 이미지`} />
              <div>
                <strong>{item.productName}</strong>
                <small>{[item.purpose, item.mood, item.quantity ? `${item.quantity}개` : ""].filter(Boolean).join(" / ")}</small>
              </div>
            </button>
          </article>
        ))}
      </div>
    </MobileShell>
  );
}
export function QuoteDetailScreen() {
  const id = new URL(location.href).searchParams.get("id");
  const { data: quote, loading, error } = useQuote(id);
  if (loading) return <MobileShell className="quote-detail-screen"><TopNav title="견적 요청 상세" fallback="quotes" /><p className="product-data-state">견적을 불러오는 중입니다.</p></MobileShell>;
  if (!quote || error) return <MobileShell className="quote-detail-screen"><TopNav title="견적 요청 상세" fallback="quotes" /><p className="product-data-state">{error || "견적을 찾을 수 없습니다."}</p></MobileShell>;
  const raw = quote as Record<string, unknown>;
  const offers = Array.isArray(quote.offers) ? quote.offers : [];
  const latestOffer = offers[0] as Record<string, unknown> | undefined;
  return (
    <MobileShell className="quote-detail-screen">
      <TopNav title="견적 요청 상세" fallback="quotes" />
      <section className="quote-detail-content">
        <DetailSection
          title="기본 정보"
          rows={[
            ["요청번호", quote.managementCode],
            ["회원명", String(raw.user_name || "-")],
            ["회원 연락처", String(raw.user_phone || "-")],
          ]}
        />
        <DetailSection
          title="요청 정보"
          rows={[
            ["용도", quote.purpose || "-"],
            ["분위기", String(quote.mood || "-")],
            ["예산", quote.budgetMin != null || quote.budgetMax != null ? `${Number(quote.budgetMin || 0).toLocaleString()}원 ~ ${quote.budgetMax != null ? `${quote.budgetMax.toLocaleString()}원` : "이상"}` : "-"],
            ["수량", quote.quantity ? `${quote.quantity}개` : "-"],
            ["추가 요청사항", String(raw.additional_request || "없습니다.")],
            ["첨부 파일", raw.attachment_file_id ? "첨부파일 있음" : "없음"],
          ]}
        />
        <section className="detail-section package-detail">
          <h2>패키징 추천 정보</h2>
          <dl>
            <div>
              <dt>제품명</dt>
              <dd>{String(raw.recommendation_name || "추천 결과 준비 중")}</dd>
            </div>
            <div>
              <dt>패키징 방향</dt>
              <dd>{String(raw.recommendation_direction || "담당자가 추천 방향을 준비하고 있습니다.")}</dd>
            </div>
            <div>
              <dt>추천 구성</dt>
              <dd>{String(raw.recommendation_composition || "-")}</dd>
            </div>
            <div>
              <dt>패키징 포인트</dt>
              <dd>{String(raw.recommendation_points || "-")}</dd>
            </div>
          </dl>
        </section>
        <DetailSection
          title="견적 요청 정보"
          rows={[
            ["희망 납기일", quote.desiredDeliveryDate || "-"],
            ["추가 요청사항", String(raw.additional_request || "없습니다.")],
          ]}
        />
        {latestOffer && <section className="detail-section received-quote">
          <h2>수신 견적</h2>
          <div className="received-quote__product">
            <img src={String(latestOffer.image_url || "/assets/product-3.png")} alt={quote.productName} />
            <div>
              <strong>{quote.productName}</strong>
              <small>등록일 {String(latestOffer.created_at || "").slice(0, 10)}</small>
              <b>{Number(latestOffer.total_amount || 0).toLocaleString()}원</b>
            </div>
          </div>
          <button type="button" onClick={() => navigate("quoteOfferDetail", { id: quote.id })}>
            상세보기
          </button>
        </section>}
      </section>
    </MobileShell>
  );
}

export function QuoteOfferDetailScreen() {
  const id = new URL(location.href).searchParams.get("id");
  const { data: quote, loading, error } = useQuote(id);
  const [consultationDone, setConsultationDone] = useState(false);
  if (loading) return <MobileShell className="quote-offer-detail-screen"><TopNav title="견적 상세" fallback="quotes" /><p className="product-data-state">견적을 불러오는 중입니다.</p></MobileShell>;
  const offer = quote && Array.isArray(quote.offers) ? quote.offers[0] as Record<string, unknown> | undefined : undefined;
  if (!quote || !offer || error) return <MobileShell className="quote-offer-detail-screen"><TopNav title="견적 상세" fallback="quotes" /><p className="product-data-state">{error || "수신된 견적이 없습니다."}</p></MobileShell>;
  const costs = [
    ["제조 원가", moneyValue(offer.manufacture_cost)],
    ["포장비", moneyValue(offer.packaging_cost)],
    ["스티커", moneyValue(offer.sticker_cost)],
    ["디자인비", moneyValue(offer.design_cost)],
    ["배송비", moneyValue(offer.delivery_cost)],
    ["기타 금액", moneyValue(offer.other_cost)],
  ];
  return (
    <MobileShell className="quote-offer-detail-screen">
      <TopNav title="견적 상세" fallback="quoteDetail" />
      <section className="quote-offer-content">
        <h1>견적이 도착했습니다!</h1>
        <p>
          내용을 확인하신 후,
          <br />
          진행을 원하시면 계약 상담을 요청해 주세요.
        </p>
        <article className="quote-ticket">
          <header>
            <span>{quote.productName}</span>
            <strong>
              {Number(offer.total_amount || 0).toLocaleString()}<small>원</small>
            </strong>
            <p>제작기간 {String(offer.production_days || 0)}일 · 수량 {String(offer.quantity || 0)}개</p>
          </header>
          <img
            src={String(offer.image_url || "/assets/quote-offer-product.png")}
            alt={quote.productName}
          />
          <dl className="quote-costs">
            {costs.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <dl className="quote-options">
            <div>
              <dt>포장 구성</dt>
              <dd>{String(offer.packaging_composition || "-")}</dd>
            </div>
            <div>
              <dt>상품 구성</dt>
              <dd>{String(offer.product_composition || "-")}</dd>
            </div>
            <div>
              <dt>기타 안내 사항</dt>
              <dd>없음</dd>
            </div>
          </dl>
        </article>
      </section>
      <FixedButton onClick={async () => {
        await requestApi(`/quotes/${quote.id}/consultation`, { method: "POST" });
        setConsultationDone(true);
      }}>
        계약 상담 요청
      </FixedButton>
      {consultationDone && <AppDialog title="상담 요청이 완료되었습니다." description="담당자가 확인 후 연락드리겠습니다." onConfirm={() => navigate("quotes", { source: "my" })} />}
    </MobileShell>
  );
}

function DetailSection({ title, rows }: { title: string; rows: string[][] }) {
  return (
    <section className="detail-section">
      <h2>{title}</h2>
      <dl>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
export function PaymentScreen() {
  const productId = new URL(location.href).searchParams.get("productId");
  const { data: product, loading: productLoading } = useProduct(productId);
  const { data: fallbackProducts, loading: listLoading } = useProducts({ pageSize: 1 });
  const { data: user } = useCurrentUser();
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [deliveryRequest, setDeliveryRequest] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  useEffect(() => {
    if (!user) return;
    setRecipientName((value) => value || user.name || "");
    setRecipientPhone((value) => value || formatPhone(user.phone || ""));
    setPostalCode((value) => value || user.postalCode || "");
    setAddressLine1((value) => value || user.addressLine1 || "");
    setAddressLine2((value) => value || user.addressLine2 || "");
  }, [user]);
  const direct = Boolean(productId);
  const displayProduct = product || fallbackProducts[0];
  if (productLoading || listLoading) return <MobileShell className="payment-screen"><TopNav title="결제하기" fallback={direct ? "productDetail" : "quoteDetail"} /><p className="product-data-state">결제 정보를 불러오는 중입니다.</p></MobileShell>;
  if (!displayProduct) return <MobileShell className="payment-screen"><TopNav title="결제하기" fallback={direct ? "productDetail" : "quoteDetail"} /><p className="product-data-state">결제할 상품을 찾을 수 없습니다.</p></MobileShell>;
  const name = displayProduct.name;
  const amount = displayProduct.price;
  return (
    <MobileShell className="payment-screen">
      <TopNav
        title="결제하기"
        fallback={direct ? "productDetail" : "quoteDetail"}
      />
      <section className="payment-page">
        <div className="payment-info-section">
          <h2>결제 정보</h2>
          <div className="payment-product">
            <img src={displayProduct.image} alt={displayProduct.name} />
            <div>
              <strong>{name}</strong>
              <span>{amount}</span>
            </div>
          </div>
          <div className="payment-guide">
            <b>[안내사항]</b>
            <p>
              교환/반품 및 환불 관련 자세한 안내는 유선 고객센터를 통해
              문의해주시길 바랍니다.
            </p>
          </div>
        </div>
        <div className="payment-section payment-shipping">
          <h2>배송 정보</h2>
          <div className="payment-fields">
            <div className="payment-field">
              <label>수령자명</label>
              <input value={recipientName} onChange={(event) => setRecipientName(event.target.value)} placeholder="수령자 성함을 입력해 주세요." />
            </div>
            <div className="payment-field">
              <label>수령자 연락처</label>
              <input
                value={recipientPhone}
                onChange={(event) => setRecipientPhone(formatPhone(event.target.value))}
                inputMode="tel"
                placeholder="수령자 연락처를 입력해 주세요."
              />
            </div>
            <div className="payment-field payment-field--address">
              <label>배송지</label>
              <div className="address-row">
                <input value={addressLine1} readOnly placeholder="주소를 검색해 주세요." />
                <button
                  type="button"
                  onClick={() => {
                    setPaymentError("");
                    void openPostcodeSearch((result) => {
                      setPostalCode(result.zonecode);
                      setAddressLine1(result.roadAddress || result.jibunAddress);
                    }).catch((reason) => {
                      setPaymentError(reason instanceof Error ? reason.message : "주소검색 창을 열지 못했습니다.");
                    });
                  }}
                >
                  주소 검색
                </button>
              </div>
              <input value={addressLine2} onChange={(event) => setAddressLine2(event.target.value)} placeholder="상세 주소를 입력해 주세요." />
            </div>
            <div className="payment-field">
              <label>배송 메시지</label>
              <input value={deliveryRequest} onChange={(event) => setDeliveryRequest(event.target.value)} placeholder="배송 메시지를 입력해 주세요." />
            </div>
          </div>
        </div>
        <div className="payment-section payment-amount">
          <h2>결제 금액</h2>
          <dl>
            <div>
              <dt>판매 금액</dt>
              <dd>{amount}</dd>
            </div>
            <div className="total">
              <dt>총 결제 금액</dt>
              <dd>{amount}</dd>
            </div>
          </dl>
        </div>
        <div className="payment-section payment-consent">
          <h2>결제 동의</h2>
          {[
            "전체 동의",
            "[필수] 개인 정보 수집 및 이용 동의",
            "[필수] 이용 약관 동의",
            "[필수] 결제 동의",
          ].map((label) => (
            <label className="payment-agree" key={label}>
              <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </section>
      {paymentError && <p className="form-error fixed-action-error">{paymentError}</p>}
      <div className="payment-submit">
        <button type="button" disabled={!productId || !recipientName || !recipientPhone || !addressLine1 || !agreed || submitting} onClick={async () => {
          setSubmitting(true); setPaymentError("");
          try {
            const config = await requestApi<{ clientKey: string }>("/payments/config");
            const order = await requestApi<{ id: string; orderNumber: string; totalAmount: number; productName: string }>("/orders", { method: "POST", body: JSON.stringify({
              productId, quantity: 1, recipientName, recipientPhone, postalCode, addressLine1, addressLine2, deliveryRequest,
            }) });
            await loadTossPayments();
            const toss = window.TossPayments?.(config.clientKey);
            if (!toss) throw new Error("결제창을 실행할 수 없습니다.");
            const successUrl = new URL("/", window.location.origin);
            successUrl.searchParams.set("view", "paymentSuccess");
            successUrl.searchParams.set("orderDbId", order.id);
            const failUrl = new URL("/", window.location.origin);
            failUrl.searchParams.set("view", "paymentFail");
            failUrl.searchParams.set("orderDbId", order.id);
            failUrl.searchParams.set("productId", String(productId));
            const paymentRequest = {
              orderId: order.orderNumber,
              orderName: order.productName,
              customerName: recipientName,
              customerEmail: user?.email,
              customerMobilePhone: recipientPhone.replace(/\D/g, ""),
              successUrl: successUrl.toString(),
              failUrl: failUrl.toString(),
            };
            const customerKey = user?.id || "ANONYMOUS";
            if (/^(test|live)_gck_/.test(config.clientKey)) {
              const widgets = toss.widgets({ customerKey });
              await widgets.setAmount({ currency: "KRW", value: order.totalAmount });
              const paymentWindow = await widgets.renderPaymentWindow();
              paymentWindow.on("paymentRequest", async () => {
                try { await widgets.requestPayment(paymentRequest); }
                catch (reason) { setPaymentError(reason instanceof Error ? reason.message : "결제 요청에 실패했습니다."); }
              });
            } else {
              const payment = toss.payment({ customerKey });
              await payment.requestPayment({
                method: "CARD",
                amount: { currency: "KRW", value: order.totalAmount },
                ...paymentRequest,
              });
            }
          } catch (reason) { setPaymentError(reason instanceof Error ? reason.message : "주문 생성에 실패했습니다."); }
          finally { setSubmitting(false); }
        }}>
          {submitting ? "처리 중" : "결제하기"}
        </button>
      </div>
    </MobileShell>
  );
}

export function PaymentSuccessScreen() {
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [message, setMessage] = useState("결제를 승인하고 있습니다.");
  useEffect(() => {
    const url = new URL(window.location.href);
    const paymentKey = url.searchParams.get("paymentKey");
    const orderId = url.searchParams.get("orderId");
    const amount = Number(url.searchParams.get("amount"));
    const orderDbId = url.searchParams.get("orderDbId");
    if (!paymentKey || !orderId || !amount || !orderDbId) { setState("failed"); setMessage("결제 승인정보가 올바르지 않습니다."); return; }
    requestApi("/payments/confirm", { method: "POST", body: JSON.stringify({ paymentKey, orderId, amount, orderDbId, idempotencyKey: `confirm-${orderId}` }) })
      .then(() => navigate("my"))
      .catch((reason) => { setState("failed"); setMessage(reason instanceof Error ? reason.message : "결제 승인에 실패했습니다."); });
  }, []);
  return <MobileShell className="complete-state"><h2>{message}</h2>{state === "working" ? <div className="loading-indicator" /> : <button type="button" onClick={() => navigate(state === "done" ? "paymentHistory" : "payment")}>{state === "done" ? "결제 내역 확인" : "결제 다시 시도"}</button>}</MobileShell>;
}

export function PaymentFailScreen() {
  const url = new URL(window.location.href);
  const orderDbId = url.searchParams.get("orderDbId");
  const productId = url.searchParams.get("productId");
  const code = url.searchParams.get("code") || "PAYMENT_FAILED";
  const message = url.searchParams.get("message") || "결제 정보를 확인한 후 다시 시도해 주세요.";
  useEffect(() => {
    if (!orderDbId) return;
    requestApi("/payments/fail", { method: "POST", body: JSON.stringify({ orderDbId, code, message }) }).catch(() => undefined);
  }, [code, message, orderDbId]);
  return <MobileShell className="complete-state"><h2>결제가 완료되지 않았습니다.</h2><p>{message}</p><button type="button" onClick={() => navigate("payment", productId ? { productId } : undefined)}>결제 다시 시도</button></MobileShell>;
}
export function PaymentHistoryScreen() {
  const { data: orders, loading, error } = useOrders();
  return (
    <MobileShell className="payment-history-screen">
      <TopNav title="결제 내역" fallback="my" />
      <div className="payment-history-list">
        {loading && <p className="product-data-state">결제 내역을 불러오는 중입니다.</p>}
        {!loading && error && <p className="product-data-state">{error}</p>}
        {!loading && !error && !orders.length && <p className="product-data-state">결제 내역이 없습니다.</p>}
        {orders.map((order) => (
          <article key={order.id}>
            <header>
              <strong>{order.orderedAt.slice(0, 10)}</strong>
              <button type="button" onClick={() => navigate("paymentDetail", { id: order.id })}>
                상세보기
              </button>
            </header>
            <button
              className="payment-history-product"
              type="button"
              onClick={() => navigate("paymentDetail", { id: order.id })}
            >
              <img src="/assets/product-1.png" alt="" />
              <span>
                <small>{orderStatusLabel(order.status)}</small>
                <strong>{order.productName}</strong>
                <b>{order.totalAmount.toLocaleString()}원</b>
              </span>
            </button>
          </article>
        ))}
      </div>
    </MobileShell>
  );
}
export function PaymentDetailScreen() {
  const [open, setOpen] = useState({ shipping: false, payment: false });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const id = new URL(location.href).searchParams.get("id");
  const { data: order, loading, error } = useOrder(id);
  if (loading) return <MobileShell className="payment-detail-screen"><TopNav title="결제 내역 상세" fallback="paymentHistory" /><p className="product-data-state">결제 내역을 불러오는 중입니다.</p></MobileShell>;
  if (!order || error) return <MobileShell className="payment-detail-screen"><TopNav title="결제 내역 상세" fallback="paymentHistory" /><p className="product-data-state">{error || "결제 내역을 찾을 수 없습니다."}</p></MobileShell>;
  const detail = order as Record<string, unknown>;
  const accordionSections = [
    {
      key: "shipping" as const,
      label: "배송 정보",
      rows: [
        ["수령자명", String(detail.recipient_name || order.recipientName || "-")],
        ["수령자 연락처", String(detail.recipient_phone || order.recipientPhone || "-")],
        ["배송지", `${String(detail.address_line1 || order.addressLine1 || "")} ${String(detail.address_line2 || order.addressLine2 || "")}`.trim() || "-"],
        ["배송 메시지", String(detail.delivery_request || "없음")],
        ["택배사", String(detail.carrier_name || "-")],
        ["송장번호", String(detail.tracking_number || "-")],
      ],
    },
    {
      key: "payment" as const,
      label: "결제 정보",
      rows: [
        ["결제번호", order.orderNumber],
        ["결제상태", paymentStatusLabel(detail.payment_status || order.status)],
        ["결제수단", String(detail.payment_method || "-")],
        ["결제일시", order.orderedAt],
        ["최종 결제 금액", `${order.totalAmount.toLocaleString()}원`],
      ],
    },
  ];
  return (
    <MobileShell className="payment-detail-screen">
      <TopNav title="결제 내역 상세" fallback="paymentHistory" />
      <section className="payment-detail-content">
        <h2>상품 정보</h2>
        <div className="payment-detail-product">
          <img src="/assets/product-1.png" alt="수제 한과 선물세트" />
          <div>
            <small>선물세트</small>
            <strong>{order.productName}</strong>
            <b>{order.totalAmount.toLocaleString()}원</b>
          </div>
        </div>
        <section className="order-info">
          <h2>주문 정보</h2>
          <dl>
            <div>
              <dt>이름</dt>
              <dd>{String(detail.user_name || "-")}</dd>
            </div>
            <div>
              <dt>휴대전화 번호</dt>
              <dd>{String(detail.user_phone || "-")}</dd>
            </div>
            <div>
              <dt>주문 번호</dt>
              <dd>{order.orderNumber}</dd>
            </div>
          </dl>
          {order.status === "ordered" && <button type="button" onClick={() => setCancelOpen(true)}>주문 취소</button>}
        </section>
        {accordionSections.map((section) => (
          <section className="payment-accordion" key={section.key}>
            <button
              type="button"
              onClick={() =>
                setOpen((current) => ({
                  ...current,
                  [section.key]: !current[section.key],
                }))
              }
            >
              <strong>{section.label}</strong>
              <ChevronDown
                size={20}
                className={open[section.key] ? "is-open" : ""}
              />
            </button>
            {open[section.key] && (
              <dl>
                {section.rows.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </section>
      {cancelOpen && <AppDialog
        title="주문을 취소하시겠습니까?"
        description="주문확인 이후에는 사용자가 직접 취소할 수 없습니다."
        confirmText={cancelling ? "취소 처리 중" : "주문 취소"}
        cancelText="닫기"
        onCancel={() => setCancelOpen(false)}
        onConfirm={async () => {
          if (cancelling) return;
          setCancelling(true);
          try {
            await requestApi(`/orders/${order.id}`, { method: "PUT", body: JSON.stringify({ status: "cancelled" }) });
            navigate("paymentHistory");
          } finally {
            setCancelling(false);
          }
        }}
      />}
    </MobileShell>
  );
}
